import { Component, FC, ReactNode, useEffect, useState } from 'react';
import { DatePicker } from 'antd';
import { RangePickerProps } from 'antd/lib/date-picker/generatePicker';
import { CommonPickerMethods } from 'antd/lib/date-picker/generatePicker/interface';
import moment, { Moment } from 'moment';

declare type EventValue<Moment> = Moment | null;
export declare type RangeValue<Moment> = [EventValue<Moment>, EventValue<Moment>] | null;

const { RangePicker } = DatePicker;

export const CustomDatePicker: FC<{
    dates?: RangeValue<Moment>;
    setDates?: (dates?: RangeValue<Moment>) => void;
    onDatesSelected: (startDate: string, endDate: string) => void;
    noOfDays: number;
    enableFuture?: boolean;
    className?: string;
    size?: 'small' | 'large' | 'middle';
    disabled?: boolean;
    style?: { [k: string]: string | number };
    newDesign?: boolean;
    separatorIcon?: ReactNode | null;
}> = ({
    onDatesSelected,
    dates,
    setDates,
    noOfDays,
    enableFuture,
    className = '',
    size = 'small',
    disabled,
    style,
    newDesign = false,
    separatorIcon = null,
}) => {
    let datePickerRef:
        | (Component<RangePickerProps<moment.Moment>> & CommonPickerMethods)
        | null = null;

    const [value, setValue] = useState<RangeValue<Moment> | undefined>(dates);
    const [open, setOpen] = useState<boolean>(false);

    useEffect(() => {
        if (!dates) {
            setValue(undefined);
        }
    }, [dates]);

    const ref = (
        ref: (Component<RangePickerProps<moment.Moment>> & CommonPickerMethods) | null,
    ): void => {
        datePickerRef = ref;
    };

    const onCalendarChange = (val: RangeValue<Moment>) => {
        setDates && setDates(val);
        if (val?.[0] && val[1]) {
            onDatesSelected(val[0].startOf('day').toISOString(), val[1].endOf('day').toISOString());
            datePickerRef && datePickerRef.blur();
        }
    };

    const onOpenChange = (open: boolean) => {
        if (open) {
            setOpen(true);
            setDates && setDates(([] as unknown) as RangeValue<Moment>);
        } else {
            setOpen(false);
        }
    };

    const disabledDate = (current: Moment) => {
        const tooLate = dates && dates[0] && current.diff(dates[0], 'days') >= noOfDays;
        const tooEarly = dates && dates[1] && dates[1].diff(current, 'days') >= noOfDays;
        const isFuture = current > moment().endOf('day');

        return tooEarly || tooLate || isFuture ? true : false;
    };

    const classNameToApply = `${newDesign ? 'custom-date-picker-new' : 'custom-date-picker'} ${
        value && value[0] && value[1] && dates !== undefined
            ? `${
                  newDesign ? 'bg-darwin-accent-bright-blue-800 border-none' : 'bg-actionPrimary'
              } filled-dates`
            : ''
    } ${!!className ? className : `rounded-24`}`;

    return (
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        <RangePicker
            ref={ref}
            value={open ? (([] as unknown) as RangeValue<Moment>) : value}
            disabledDate={enableFuture ? undefined : disabledDate}
            size={size}
            inputReadOnly={true}
            popupStyle={{ fontFamily: 'Lato' }}
            className={classNameToApply}
            onCalendarChange={onCalendarChange}
            onChange={(dates) => {
                setValue(dates);
            }}
            onOpenChange={onOpenChange}
            allowClear={false}
            suffixIcon={null}
            format={(date) => moment(date).format('D MMM YYYY')}
            disabled={disabled}
            style={style}
            separator={separatorIcon}
        />
    );
};
